import { useEffect, useRef } from "react";
import { Outlet, useNavigate } from "react-router";
import { LogOut } from "lucide-react";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { CurrencyProvider } from "@/hooks/useCurrency";
import { clearToken } from "@/lib/auth";
import { HealthStrip } from "@/components/HealthStrip";
import { BottomNav } from "@/components/BottomNav";
import { NotificationBell } from "@/components/NotificationBell";
import { Toaster } from "@/components/Toaster";
import { Button } from "@/components/ui/button";

export function AppShell() {
  const navigate = useNavigate();
  useLiveEvents(true);

  // M33 — the sticky sub-headers offset themselves by the header's real height. It is
  // variable (safe-area inset, the health strip wrapping or expanding), so it is measured
  // and re-measured rather than hardcoded; a ResizeObserver is what keeps the bars aligned
  // when the strip opens, not just on mount.
  const headerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const publish = () => {
      document.documentElement.style.setProperty("--app-header-h", `${header.offsetHeight}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return (
    // M26a — every authenticated screen renders money, so the currency provider wraps the
    // whole shell rather than being repeated per page.
    <CurrencyProvider>
    <div className="mx-auto flex min-h-full max-w-lg flex-col pb-24">
      <header ref={headerRef} className="sticky top-0 z-30 flex items-center justify-between gap-2 border-b border-border bg-background/95 px-4 pt-[env(safe-area-inset-top)] backdrop-blur">
        <div className="flex-1 py-3">
          <HealthStrip />
        </div>
        <NotificationBell />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            clearToken();
            navigate("/login", { replace: true });
          }}
          aria-label="Log out"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </header>

      <main className="flex-1 px-4 py-4">
        <Outlet />
      </main>

      <BottomNav />
      <Toaster />
    </div>
    </CurrencyProvider>
  );
}
